<template>
  <li
    :id="`sb-multiselect-list-item-${option.value || 0}`"
    :ref="`sb-combobox-selected-${inputId}`"
    v-tooltip="{
      label: label + (caption ? ' - ' + caption : ''),
      noDescribe: true,
    }"
    :aria-label="label"
    :aria-selected="isSelected"
    :class="computedClasses"
    class="sb-multiselect-list__item"
    role="option"
    tabindex="-1"
    value="0"
    :title="label"
    @click="handleClick"
    @keydown="handleKeyDown"
  >
    <SbCheckbox
      :model-value="isSelected"
      :disabled="isDisabled"
      :data-testid="`${dataTestid}-checkbox`"
      :indeterminate="isIndeterminate"
      :label="label"
      hide-label
      @click="handleClick"
    />

    <SbAvatar
      v-if="useAvatars"
      :key="label"
      :src="option.src"
      :name="label"
      :data-testid="`${dataTestid}-avatar`"
      size="small"
    />

    <SbIcon
      v-if="option.icon"
      class="sb-multiselect-list__item-icon"
      :data-testid="`${dataTestid}-icon`"
      :name="option.icon"
    />

    <slot name="list-item" :item="option">
      <div class="sb-multiselect-list__item-label-wrapper">
        <span
          class="sb-multiselect-list__item-label"
          :data-testid="`${dataTestid}-label`"
        >
          {{ label }}
        </span>

        <template v-if="showCaption && caption?.length">
          <span
            ref="caption"
            class="sb-multiselect-list__item-caption"
            :data-testid="`${dataTestid}-caption`"
          >
            {{ caption }}
          </span>
        </template>
      </div>
    </slot>
  </li>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { includes, isArray } from '../../../utils'
import SbAvatar from '../../Avatar'
import SbCheckbox from '../../Checkbox'
import SbIcon from '../../Icon'
import type { MultiSelectValue } from '../types'
import { Tooltip } from '../../../directives'

export default {
  name: 'SbSelectListItem',

  directives: {
    Tooltip,
  },

  components: {
    SbAvatar,
    SbCheckbox,
    SbIcon,
  },

  props: {
    inputValue: {
      type: Array as PropType<MultiSelectValue[]>,
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    modelValue: {
      type: [String, Number],
      default: '',
    },
    option: {
      type: Object,
      default: () => ({}),
    },
    itemValue: {
      type: String,
      default: 'value',
    },
    emitOption: Boolean,
    inputId: {
      type: String,
      default: null,
    },

    // Avatar properties
    useAvatars: Boolean,
    src: {
      type: String,
      default: null,
    },

    showCaption: Boolean,
    caption: {
      type: String,
      default: null,
    },

    isDisabled: Boolean,
    selectAllOption: Boolean,
    isIndeterminate: Boolean,
    allSelected: Boolean,
  },

  emits: ['emit-value'],

  computed: {
    dataTestid() {
      return this.$attrs['data-testid'] || 'sb-multiselect-list-item'
    },

    // TODO: refactor this computed because the inputValue, which is the
    // SbMultiSelect modelValue prop should be an array of string, number or Option
    isSelected() {
      return this.selectAllOption
        ? this.allSelected
        : isArray(this.inputValue)
          ? this.isValueAlreadyExists()
          : this.modelValue === this.inputValue
    },

    computedClasses() {
      return [
        this.isSelected && 'sb-multiselect-list__item--selected',
        this.isDisabled && 'sb-multiselect-list__item--disabled',
      ]
    },
  },

  methods: {
    handleEmitValue() {
      if (this.emitOption) {
        return this.$emit('emit-value', this.option)
      }

      this.$emit('emit-value', this.modelValue)
    },

    /**
     * emits the 'emit-value' event
     */
    handleClick() {
      if (this.isDisabled) return

      this.handleEmitValue()
    },

    /**
     * handle with keydown event
     * @param {Event} event
     */
    handleKeyDown(event: KeyboardEvent) {
      if (this.isDisabled) return

      if (event.key === 'Enter' || event.key === ' ') {
        this.handleEmitValue()
      }
    },

    isValueAlreadyExists() {
      if (this.emitOption) {
        const itemValue = this.modelValue

        return this.inputValue.some(($v) => {
          return typeof $v === 'object' && $v[this.itemValue] === itemValue
        })
      }

      return includes(this.inputValue, this.modelValue)
    },
  },
}
</script>
